import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { type IncrementalChange, isValueChange, type ValueChange } from '@openx/types/auditTrail';
import type { HistoryEntryDataGrouped, LabeledChange } from '@openx/utils/history/types';
import { Fragment, type ReactElement, type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { DateLabel } from '../DateLabel/DateLabel';
import {
  StyledActionTypeTypography,
  StyledChangeContainerDiv,
  StyledContainerGrid,
  StyledGroupContainerDiv,
  StyledGroupTitleTypography,
  StyledLabelTypography,
  StyledMetaSectionGrid,
  StyledWhoTypography,
} from './HistoryEntry.styled';
import { SingleFieldIncrementalChange } from './SingleFieldIncrementalChange';
import { SingleFieldValueChange } from './SingleFieldValueChange';

function isFieldValueChange(change: LabeledChange): change is LabeledChange<ValueChange> {
  return isValueChange(change);
}

type HistoryEntryProps<R> = {
  data: HistoryEntryDataGrouped;
  customInitialRender?: (resource: R) => ReactNode;
  hideEmpty: boolean;
  drawerMode?: boolean;
  handleOpenRevertDrawer?: (traceId: string) => void;
  shouldShowRevertButton?: boolean;
};

export function HistoryEntry<R>({
  data,
  customInitialRender,
  hideEmpty,
  drawerMode,
  handleOpenRevertDrawer,
  shouldShowRevertButton,
}: HistoryEntryProps<R>): ReactElement {
  const { who, when, what, mode, traceId } = data;
  const { t } = useTranslation();

  const renderSingleChange = useCallback(
    (change: LabeledChange, index: number, hideEmpty: boolean): ReactElement | null => {
      const { label, Element } = change;

      if (Element) {
        return <Fragment key={index}>{Element}</Fragment>;
      }

      const isFieldValue = isFieldValueChange(change);
      const isEmpty = isFieldValue
        ? !(change.new_value || change.old_value)
        : !((change as IncrementalChange)?.added?.length || (change as IncrementalChange)?.removed?.length);

      if (hideEmpty && isEmpty) {
        return null;
      }

      return (
        <StyledChangeContainerDiv key={index} data-test="change-container">
          <StyledLabelTypography variant="caption" color="textSecondary" component="div" data-test="change-title">
            {label}
          </StyledLabelTypography>
          {isFieldValue ? <SingleFieldValueChange change={change} /> : <SingleFieldIncrementalChange change={change} />}
        </StyledChangeContainerDiv>
      );
    },
    []
  );

  const renderFieldsSection = useCallback(() => {
    if (mode !== 'update' && customInitialRender) {
      return customInitialRender(what as unknown as R);
    }

    return what.map(({ groupTitle, changes }) => (
      <StyledGroupContainerDiv key={groupTitle}>
        <StyledGroupTitleTypography variant="h3" data-test="group-title">
          {groupTitle}
        </StyledGroupTitleTypography>
        {(changes as LabeledChange[]).map((change, idx) => renderSingleChange(change, idx, hideEmpty))}
      </StyledGroupContainerDiv>
    ));
  }, [mode, renderSingleChange, what, customInitialRender, hideEmpty]);

  return (
    <StyledContainerGrid data-test="history-row" container wrap="nowrap">
      <StyledMetaSectionGrid
        isDrawerMode={drawerMode}
        container
        size={2}
        data-test="meta-section"
        wrap="nowrap"
        sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
      >
        <StyledActionTypeTypography variant="body2" color="textSecondary" gutterBottom>
          {mode.toUpperCase()}
        </StyledActionTypeTypography>
        <StyledWhoTypography variant="body2" color="textSecondary">
          {who}
        </StyledWhoTypography>
        <Typography variant="body2" color="textSecondary" component="div">
          <DateLabel date={when} displayLocalTime={false} displayExactDate showOffset />
        </Typography>
      </StyledMetaSectionGrid>

      <Grid size={9} data-test="fields-section" sx={{ marginBottom: theme => theme.spacing(1.5) }}>
        {renderFieldsSection()}

        {shouldShowRevertButton && traceId && (
          <Button
            variant="text"
            color="primary"
            data-test="revert-changes-button"
            onClick={() => handleOpenRevertDrawer?.(traceId)}
            sx={{ mt: 2 }}
          >
            {t('Revert Changes')}
          </Button>
        )}
      </Grid>
    </StyledContainerGrid>
  );
}
